<template>
  <vs-popup
    class="bandwidth-move-modal"
    title="انتقال پکیج پهنای باند"
    :active.sync="isActive"
  >
    <div class="p-4">
      <!-- Source VM Info -->
      <div class="mb-6">
        <h4 class="text-lg font-semibold mb-2">سرور مبدا</h4>
        <vx-card class="p-4 bg-gray-100">
          <div class="grid grid-cols-2 gap-2">
            <div>
              <p class="text-sm text-gray-600">عنوان سرور:</p>
              <p class="font-semibold">{{ sourceVm.vm_title || sourceVm.vm_sequence_id }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">شناسه سرور:</p>
              <p class="font-semibold">{{ sourceVm.vm_sequence_id }}</p>
            </div>
            <div v-if="sourceBandwidthPackage">
              <p class="text-sm text-gray-600">پهنای باند فعلی:</p>
              <p class="font-semibold text-primary">
                {{ sourceBandwidthPackage.title }}
              </p>
            </div>
            <div v-if="sourceBandwidthPackage">
              <p class="text-sm text-gray-600">تاریخ انقضا:</p>
              <p class="font-semibold">{{ sourceBandwidthPackage.expired_at }}</p>
            </div>
          </div>
        </vx-card>
      </div>

      <!-- Target VM Selection -->
      <div class="mb-6">
        <h4 class="text-lg font-semibold mb-2">سرور مقصد</h4>
        
        <!-- Search Target VM -->
        <div class="mb-4">
          <vs-input
            class="w-full"
            label-placeholder="جستجو بر اساس عنوان یا شناسه"
            v-model="targetVmSearch"
            @keyup.enter="searchTargetVms"
          >
            <template #icon>
              <i class="bx bx-search"></i>
            </template>
          </vs-input>
          <vs-button
            class="mt-2"
            @click="searchTargetVms"
            :disabled="loading"
          >
            جستجو
          </vs-button>
        </div>

        <!-- Target VM List -->
        <div v-if="targetVmsList.length > 0" class="max-h-64 overflow-y-auto">
          <div
            v-for="vm in targetVmsList"
            :key="vm.id"
            class="border rounded p-3 mb-2 cursor-pointer hover:bg-gray-50 transition"
            :class="{ 'border-primary bg-blue-50': selectedTargetVm && selectedTargetVm.id === vm.id }"
            @click="selectTargetVm(vm)"
          >
            <div class="grid grid-cols-2 gap-2">
              <div>
                <p class="text-sm text-gray-600">عنوان:</p>
                <p class="font-semibold">{{ vm.vm_title || vm.vm_sequence_id }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-600">شناسه:</p>
                <p class="font-semibold">{{ vm.vm_sequence_id }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-600">وضعیت:</p>
                <p class="font-semibold">
                  <span :class="getStatusClass(vm.vm_status)">
                    {{ getStatusText(vm.vm_status) }}
                  </span>
                </p>
              </div>
              <div v-if="vm.current_bandwidth_package">
                <p class="text-sm text-gray-600">پهنای باند فعلی:</p>
                <p class="font-semibold text-warning">
                  {{ vm.current_bandwidth_package.title }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- No results -->
        <div v-else-if="searchPerformed && !loading" class="text-center py-4 text-gray-500">
          سروری یافت نشد
        </div>
      </div>

      <!-- Selected Target Info -->
      <div v-if="selectedTargetVm" class="mb-6">
        <vx-card class="p-4 border-2 border-primary">
          <h5 class="font-semibold mb-2">سرور مقصد انتخاب شده</h5>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <p class="text-sm text-gray-600">عنوان:</p>
              <p class="font-semibold">{{ selectedTargetVm.vm_title || selectedTargetVm.vm_sequence_id }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">دیتاسنتر:</p>
              <p class="font-semibold">{{ selectedTargetVm.datacenter_name }}</p>
            </div>
          </div>
          
          <!-- Warning if target has bandwidth -->
          <vs-alert
            v-if="selectedTargetVm.current_bandwidth_package"
            color="warning"
            class="mt-3"
          >
            <template #title>
              هشدار
            </template>
            این سرور از قبل پکیج پهنای باند دارد ({{ selectedTargetVm.current_bandwidth_package.title }}).
            با پکیج جدید جایگزین خواهد شد.
          </vs-alert>
        </vx-card>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-2 mt-6">
        <vs-button
          color="danger"
          type="flat"
          @click="cancel"
        >
          لغو
        </vs-button>
        <vs-button
          color="primary"
          @click="confirmMove"
          :disabled="!selectedTargetVm || loading"
          :loading="loading"
        >
          انتقال پهنای باند
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: 'MoveBandwidthPackageModal',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    sourceVm: {
      type: Object,
      default: () => ({})
    },
    sourceBandwidthPackage: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isActive: false,
      targetVmSearch: '',
      targetVmsList: [],
      selectedTargetVm: null,
      loading: false,
      searchPerformed: false
    }
  },
  watch: {
    active(val) {
      this.isActive = val
      if (val) {
        this.resetModal()
      }
    },
    isActive(val) {
      this.$emit('update:active', val)
    }
  },
  methods: {
    async searchTargetVms() {
      if (!this.targetVmSearch) {
        this.$vs.notify({
          title: 'خطا',
          text: 'لطفاً عبارت جستجو را وارد کنید',
          color: 'danger'
        })
        return
      }

      this.loading = true
      this.searchPerformed = true

      try {
        const response = await this.$http.get('admin/cloud/vm', {
          params: {
            filter: this.targetVmSearch,
            user_id: this.sourceVm.user_id
          }
        })

        if (response.data.status) {
          // Filter out source VM and VMs not in same datacenter
          this.targetVmsList = response.data.params.data.filter(vm => {
            return vm.id !== this.sourceVm.id &&
                   vm.datacenter_id === this.sourceVm.datacenter_id &&
                   [4, 5].includes(vm.vm_status)
          })
        }
      } catch (error) {
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در جستجوی سرورها',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    selectTargetVm(vm) {
      this.selectedTargetVm = vm
    },

    async confirmMove() {
      if (!this.selectedTargetVm) {
        return
      }

      this.loading = true

      try {
        const response = await this.$http.post('admin/cloud/vm/bandwidth/move', {
          source_vm_id: this.sourceVm.id,
          target_vm_id: this.selectedTargetVm.id
        })

        if (response.data.status) {
          this.$vs.notify({
            title: 'موفق',
            text: response.data.message,
            color: 'success'
          })

          this.$emit('success', response.data.params)
          this.isActive = false
        }
      } catch (error) {
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در انتقال پهنای باند',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    cancel() {
      this.isActive = false
    },

    resetModal() {
      this.targetVmSearch = ''
      this.targetVmsList = []
      this.selectedTargetVm = null
      this.searchPerformed = false
    },

    getStatusClass(status) {
      const statusMap = {
        1: 'text-gray-500',
        2: 'text-yellow-500',
        3: 'text-blue-500',
        4: 'text-green-500',
        5: 'text-red-500',
        6: 'text-orange-500',
        7: 'text-purple-500',
        8: 'text-gray-400',
        9: 'text-red-700'
      }
      return statusMap[status] || 'text-gray-500'
    },

    getStatusText(status) {
      const statusMap = {
        1: 'در حال ایجاد',
        2: 'در حال راه‌اندازی',
        3: 'در حال خاموش شدن',
        4: 'آنلاین',
        5: 'خاموش',
        6: 'در حال ریبوت',
        7: 'در حال نصب مجدد',
        8: 'حذف شده',
        9: 'خطا'
      }
      return statusMap[status] || 'نامشخص'
    }
  }
}
</script>

<style scoped>
.bandwidth-move-modal >>> .vs-popup {
  width: 90%;
  max-width: 800px;
}
</style>
